import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import searchFill from '@iconify/icons-eva/search-fill';
import playCircleFill from '@iconify/icons-eva/play-circle-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import helpContent from './helpContent';
import { useTour } from '../TourProvider';

function matchesQuery(article, query) {
  if (!query) return true;
  const haystack = [article.title, ...(article.steps || []), article.note || '']
    .join(' ')
    .toLowerCase();
  return haystack.includes(query.toLowerCase());
}

export default function HelpDrawer({ open, onClose }) {
  const [query, setQuery] = useState('');
  const { start, availableTours } = useTour();

  const categories = useMemo(
    () =>
      helpContent
        .map((category) => ({
          ...category,
          articles: category.articles.filter((article) => matchesQuery(article, query)),
        }))
        .filter((category) => category.articles.length > 0),
    [query]
  );

  const handleStart = (tourId) => {
    onClose();
    start(tourId);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 2 }}
      >
        <Typography variant="h6">Need help?</Typography>
        <IconButton onClick={onClose}>
          <Icon icon={closeFill} width={20} height={20} />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2.5, pb: 2 }}>
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search help…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon={searchFill} width={18} height={18} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {!query && availableTours.length > 0 && (
        <Box sx={{ px: 2.5, pb: 2 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Tours for this page
          </Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {availableTours.map((tour) => (
              <Box
                key={tour.id}
                onClick={() => handleStart(tour.id)}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                  '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12) },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ color: 'primary.main', display: 'flex' }}>
                    <Icon icon={playCircleFill} width={20} height={20} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>
                      {tour.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {tour.steps.length} steps
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <Box sx={{ px: 2.5, pb: 4, overflowY: 'auto' }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          {query ? 'Results' : 'Browse'}
        </Typography>

        {categories.length === 0 && (
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            Nothing matches “{query}”.
          </Typography>
        )}

        {categories.map((category) => (
          <Accordion
            key={category.id}
            defaultExpanded={Boolean(query)}
            disableGutters
            elevation={0}
            sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
          >
            <AccordionSummary
              expandIcon={<Icon icon={arrowIosDownwardFill} width={18} height={18} />}
              sx={{ px: 0 }}
            >
              <Typography variant="subtitle2">{category.title}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              <Stack spacing={2.5}>
                {category.articles.map((article) => (
                  <Box key={article.id}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {article.title}
                    </Typography>
                    <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
                      {(article.steps || []).map((step) => (
                        <Typography
                          key={step}
                          component="li"
                          variant="body2"
                          sx={{ color: 'text.secondary', mb: 0.5 }}
                        >
                          {step}
                        </Typography>
                      ))}
                    </Box>
                    {article.note && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 1,
                          p: 1.25,
                          borderRadius: 1,
                          borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
                          bgcolor: (theme) => alpha(theme.palette.primary.lighter, 0.35),
                          color: 'text.primary',
                          lineHeight: 1.55,
                        }}
                      >
                        {article.note}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Drawer>
  );
}

HelpDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
